import React, { useEffect, useMemo, useState } from "react";
import {
  Radio, RefreshCw, DollarSign, LogIn, AlertTriangle, Gem, Activity,
  TrendingUp, Loader2, ArrowUp, ArrowDown, Gauge,
} from "lucide-react";
import { brand } from "../mock";
import TickerTape from "../components/TickerTape";
import LivePricesTV from "../components/LivePricesTV";
import InstrumentCard from "../components/InstrumentCard";
import { groupCompletedTrades } from "../utils/mt5Trades";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `${n < 0 ? "-" : "+"}$${Math.abs(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// The 5 core instruments the Command Center tracks.
const INSTRUMENTS = [
  { key: "XAUUSD", label: "Gold", sub: "XAUUSD · Metal", badge: "AU", logo: "/brokers/xauusd.png", tv: "OANDA:XAUUSD", match: (b) => b.startsWith("XAU") || b.startsWith("GOLD") },
  { key: "BTC", label: "Bitcoin", sub: "BTCUSD · Crypto", badge: "₿", logo: "/brokers/btc.png", tv: "BINANCE:BTCUSDT", match: (b) => b.startsWith("BTC") },
  { key: "ETH", label: "Ethereum", sub: "ETHUSD · Crypto", badge: "Ξ", logo: "/brokers/eth.png", tv: "BINANCE:ETHUSDT", match: (b) => b.startsWith("ETH") },
  { key: "XAG", label: "Silver", sub: "XAGUSD · Metal", badge: "AG", logo: "/brokers/silver.png", tv: "OANDA:XAGUSD", match: (b) => b.startsWith("XAG") || b.startsWith("SILVER") },
];

const RANGES = [["7", "7D"], ["30", "30D"], ["90", "90D"], ["all", "All"]];
const instKey = (symbol) => {
  const base = String(symbol || "").toUpperCase().replace(/[^A-Z]/g, "");
  const found = INSTRUMENTS.find((i) => i.match(base));
  return found ? found.key : null;
};

function filterRange(trades, range) {
  if (range === "all") return trades;
  const days = { "7": 7, "30": 30, "90": 90 }[range] || 3650;
  const cut = Date.now() / 1000 - days * 86400;
  return trades.filter((t) => t.ts && t.ts >= cut);
}

function computeInstStats(list) {
  if (!list.length) return { trades: 0 };
  const r = list.map((t) => t.realized);
  const wins = r.filter((x) => x > 0), losses = r.filter((x) => x < 0);
  const decided = wins.length + losses.length;
  const gw = wins.reduce((a, b) => a + b, 0), gl = Math.abs(losses.reduce((a, b) => a + b, 0));
  return {
    trades: list.length,
    net: Math.round(r.reduce((a, b) => a + b, 0) * 100) / 100,
    winRate: decided ? Math.round((wins.length / decided) * 100) : 0,
    pf: gl > 0 ? Math.round((gw / gl) * 100) / 100 : gw > 0 ? 999 : 0,
    avgWin: wins.length ? Math.round((gw / wins.length) * 100) / 100 : 0,
    avgLoss: losses.length ? Math.round((losses.reduce((a, b) => a + b, 0) / losses.length) * 100) / 100 : 0,
    best: r.length ? Math.max(...r) : 0,
    worst: r.length ? Math.min(...r) : 0,
    lastTs: Math.max(...list.map((t) => t.ts || 0)),
  };
}

const today = new Date().toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "short", year: "numeric" });

const CommandCenter = ({ user, onSignIn }) => {
  const [trades, setTrades] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [range, setRange] = useState("30");
  const [pulse, setPulse] = useState(null);
  const [pulseLoading, setPulseLoading] = useState(true);
  const [live, setLive] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const d = await (await fetch(`${API}/mt5/trades`, { credentials: "include" })).json();
        setTrades(groupCompletedTrades(d.trades || []).filter((t) => instKey(t.symbol)));
      } catch { /* not signed in */ }
      setLoaded(true);
    })();
    loadPulse(false);
  }, [user]);

  const loadPulse = async (force) => {
    setPulseLoading(true);
    try {
      const d = await (await fetch(`${API}/mt5/command-pulse${force ? "?nocache=1" : ""}`, { credentials: "include" })).json();
      setPulse(d.pulse || null);
      const lv = {};
      (d.instruments || []).forEach((x) => { lv[x.key] = x; });
      setLive(lv);
    } catch { /* */ }
    setPulseLoading(false);
  };

  // Group filtered trades by instrument key.
  const byInst = useMemo(() => {
    const filtered = filterRange(trades, range);
    const map = {};
    INSTRUMENTS.forEach((i) => (map[i.key] = []));
    filtered.forEach((t) => { const k = instKey(t.symbol); if (k) map[k].push(t); });
    return map;
  }, [trades, range]);

  const stats = useMemo(() => {
    const s = {};
    INSTRUMENTS.forEach((i) => (s[i.key] = computeInstStats(byInst[i.key] || [])));
    return s;
  }, [byInst]);

  // Aggregate summary across the 5 core assets (in range).
  const summary = useMemo(() => {
    const all = INSTRUMENTS.flatMap((i) => byInst[i.key] || []);
    return computeInstStats(all);
  }, [byInst]);

  // Recent real activity across the 5 core assets.
  const recent = useMemo(() => filterRange(trades, range).slice(0, 12), [trades, range]);
  const connected = !!user;

  return (
    <div>
      <TickerTape />
      <div className="max-w-[1200px] mx-auto px-8 py-8">
        {/* Top row */}
        <div className="flex items-center justify-between mb-6 ws-fade-up">
          <div className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase font-medium">{today}</div>
          <div className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[12px] font-medium flex items-center gap-2">
            <img src="/wsj-logo.webp" alt="" className="w-4 h-4 rounded-full inline-block align-middle" /> {brand.name}
          </div>
        </div>

        <h1 className="ws-heading text-[42px] font-bold mb-2 ws-title-gradient ws-fade-up" style={{ animationDelay: "40ms" }}>Command Center</h1>
        <p className="text-[color:var(--ws-muted)] text-sm mb-6 ws-fade-up" style={{ animationDelay: "60ms" }}>
          Your macro desk for <span className="text-[color:var(--ws-blue-bright)] font-medium">XAUUSD · BTC · ETH · XAG</span> — live prices, AI market pulse, and your real edge on each.
        </p>

        {/* Core-asset summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5 ws-fade-up" style={{ animationDelay: "80ms" }} data-testid="cc-summary-strip">
          <div className="ws-panel p-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-muted)] mb-1 flex items-center gap-1.5"><DollarSign className="w-3 h-3" /> Net on core assets</div>
            <div className={`ws-heading text-[24px] font-bold ${summary.net > 0 ? "ws-up" : summary.net < 0 ? "ws-down" : "text-white"}`}>{connected && summary.trades ? money(summary.net) : "—"}</div>
          </div>
          <div className="ws-panel p-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-muted)] mb-1 flex items-center gap-1.5"><Gauge className="w-3 h-3" /> Win rate</div>
            <div className="ws-heading text-[24px] font-bold text-white">{connected && summary.trades ? `${summary.winRate}%` : "—"}</div>
          </div>
          <div className="ws-panel p-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-muted)] mb-1 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Profit factor</div>
            <div className="ws-heading text-[24px] font-bold text-[color:var(--ws-blue-bright)]">{connected && summary.trades ? (summary.pf >= 999 ? "∞" : summary.pf) : "—"}</div>
          </div>
          <div className="ws-panel p-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-muted)] mb-1 flex items-center gap-1.5"><Activity className="w-3 h-3" /> Trades</div>
            <div className="ws-heading text-[24px] font-bold text-white">{connected && summary.trades ? summary.trades : "—"}</div>
          </div>
        </div>

        {/* Range selector */}
        <div className="flex items-center gap-2 mb-5 ws-fade-up" style={{ animationDelay: "100ms" }}>
          <span className="text-[11px] text-[color:var(--ws-muted)] uppercase tracking-wider mr-1">Edge window</span>
          {RANGES.map(([v, l]) => (
            <button key={v} onClick={() => setRange(v)} data-testid={`cc-range-${v}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${range === v ? "ws-btn-primary" : "ws-btn-ghost"}`}>{l}</button>
          ))}
        </div>

        {/* AI Market Pulse */}
        <div className="ws-panel p-6 mb-5 ws-fade-up" style={{ animationDelay: "120ms" }} data-testid="cc-ai-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <Radio className="w-4 h-4 text-[color:var(--ws-cyan)]" />
              <span className="text-sm font-semibold">AI Market Pulse</span>
              <span className="flex items-center gap-1.5 text-[11px] text-[color:var(--ws-muted)] ml-1"><span className="ws-live-dot" /> Live · Grounded in real prices</span>
            </div>
            <button onClick={() => loadPulse(true)} disabled={pulseLoading} data-testid="cc-pulse-refresh" className="ws-btn-ghost px-2.5 py-1.5 rounded-lg">
              {pulseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 hover:rotate-180 transition-transform duration-500" />}
            </button>
          </div>

          {pulseLoading && !pulse ? (
            <div className="text-[color:var(--ws-muted)] text-sm py-6 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading the tape…</div>
          ) : !pulse ? (
            <div className="text-[color:var(--ws-muted)] text-sm py-6 text-center">Market pulse unavailable right now.</div>
          ) : (
            <>
              <h3 className="ws-heading text-[19px] font-semibold mb-1.5">{pulse.headline}</h3>
              <div className="text-xs mb-4"><span className="text-[color:var(--ws-muted)]">Bias: </span><span className="text-[color:var(--ws-blue-bright)] font-semibold">{pulse.bias}</span></div>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-2">Key Moves</div>
                  <ul className="space-y-2">
                    {(pulse.key_moves || []).map((m, i) => (
                      <li key={i} className="flex gap-2 text-[13px] text-[color:var(--ws-text)] opacity-90"><span className="text-[color:var(--ws-cyan)]">•</span>{m}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-2">Trade Ideas</div>
                  <ul className="space-y-2">
                    {(pulse.trade_ideas || []).map((m, i) => (
                      <li key={i} className="flex gap-2 text-[13px] text-[color:var(--ws-text)] opacity-90"><span className="text-[color:var(--ws-blue-bright)]">→</span>{m}</li>
                    ))}
                  </ul>
                </div>
              </div>
              {pulse.warning && (
                <div className="flex items-start gap-2.5 rounded-xl p-3 text-[13px]" style={{ background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)" }}>
                  <AlertTriangle className="w-4 h-4 text-[color:var(--ws-red)] shrink-0 mt-0.5" />
                  <span className="text-[color:var(--ws-text)] opacity-90">{pulse.warning}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Core instrument cards */}
        <div className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)] mb-3 ws-fade-up" style={{ animationDelay: "140ms" }}>Core Assets · live chart + your edge</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 ws-fade-up" style={{ animationDelay: "160ms" }} data-testid="cc-instruments">
          {INSTRUMENTS.map((inst) => (
            <InstrumentCard key={inst.key} inst={inst} stats={stats[inst.key]} live={live[inst.key]} connected={connected && loaded} />
          ))}
        </div>

        {/* Recent real activity on core assets */}
        {connected && (
          <div className="ws-panel p-6 mb-5 ws-fade-up" data-testid="cc-recent-activity">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5"><span className="ws-live-dot" /><span className="text-sm font-semibold">Recent Activity · Core Assets</span></div>
              <span className="text-[11px] text-[color:var(--ws-muted)]">Real MT5 trades</span>
            </div>
            {recent.length === 0 ? (
              <div className="text-[13px] text-[color:var(--ws-muted)] py-4 text-center">No trades on XAUUSD/BTC/ETH/XAG in this window.</div>
            ) : (
              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                {recent.map((r) => {
                  const up = r.realized >= 0;
                  const inst = INSTRUMENTS.find((i) => i.key === instKey(r.symbol));
                  return (
                    <div key={r.key} className="ws-price-cell p-3 flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${up ? "bg-[rgba(52,211,153,0.12)]" : "bg-[rgba(248,113,113,0.12)]"}`}>
                        {up ? <ArrowUp className="w-3.5 h-3.5 ws-up" /> : <ArrowDown className="w-3.5 h-3.5 ws-down" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="ws-heading font-semibold text-white text-[13px]">{r.symbol}</span>
                          <span className={`text-[10px] font-semibold ${r.side === 1 ? "ws-down" : "ws-up"}`}>{r.side === 1 ? "SELL" : "BUY"}</span>
                          <span className="text-[10px] text-[color:var(--ws-muted)]">{inst?.label}</span>
                        </div>
                        <div className="text-[11px] text-[color:var(--ws-muted)]">{r.volume ? `${r.volume} lots · ` : ""}{r.time || ""}</div>
                      </div>
                      <div className={`ws-heading font-semibold text-[13px] ${up ? "ws-up" : "ws-down"}`}>{money(r.realized)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Live Prices — customizable TradingView widget */}
        <div className="mb-5 ws-fade-up"><LivePricesTV /></div>

        {/* Sign in CTA */}
        {!user && (
          <div className="ws-panel p-8 mb-5 text-center ws-fade-up" data-testid="cc-signin-cta">
            <div className="ws-icon-chip w-14 h-14 mx-auto mb-4"><DollarSign className="w-7 h-7" /></div>
            <h3 className="ws-heading text-[22px] font-bold mb-1.5">Sign in to unlock your trading edge</h3>
            <p className="text-[color:var(--ws-muted)] text-sm mb-5">Your real win rate, P&amp;L and edge on every core asset.</p>
            <button onClick={onSignIn} className="ws-btn-primary px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2" data-testid="cc-signin-btn">
              <LogIn className="w-4 h-4" /> Sign In with Google
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 pt-6">
          <div className="ws-divider mb-4" />
          <span className="text-[11px] tracking-[0.25em] text-[color:var(--ws-muted)] uppercase inline-flex items-center gap-2">
            <Gem className="w-3.5 h-3.5 text-[color:var(--ws-blue-bright)]" /> {brand.tagline}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandCenter;
